import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/client";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { MapPin, Calendar, Clock, Briefcase } from "lucide-react";
import { glassCardHover } from "../../utils/glassStyles";
import RecruiterPageShell from "../../components/recruiter/RecruiterPageShell";
import RecruiterEmptyState from "../../components/recruiter/RecruiterEmptyState";
import Loader from "../../components/ui/Loader";

export default function PendingJobsPage() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPendingJobs();
  }, []);

  const fetchPendingJobs = async () => {
    try {
      const res = await api.get("/recruiter/jobs/pending");
      setJobs(res.data?.jobs || res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader label="Loading pending jobs..." />;

  return (
    <RecruiterPageShell
      title="Pending Approval"
      subtitle="Jobs waiting for admin review before going live"
      onBack={() => navigate("/recruiter/dashboard")}
    >
      {jobs.length === 0 ? (
        <RecruiterEmptyState
          icon={Briefcase}
          title="No pending jobs"
          description="All your submitted jobs have been reviewed."
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {jobs.map((job) => (
            <Card key={job.id} className={`rounded-2xl ${glassCardHover}`}>
              <CardContent className="p-5 sm:p-6 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-bold text-lg leading-tight">{job.title}</h3>
                  <Badge variant="secondary" className="bg-yellow-500/10 text-yellow-800 border-yellow-300/50 shrink-0">
                    <Clock className="w-3 h-3 mr-1" />
                    Pending
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" />
                  {job.location || "—"}
                </p>
                <p className="text-sm text-muted-foreground">
                  {job.type?.replace("_", " ") || "Full-time"}
                  {job.salaryMin && job.salaryMax
                    ? ` · ${job.salaryMin.toLocaleString()} – ${job.salaryMax.toLocaleString()} ETB`
                    : ""}
                </p>
                {job.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {job.tags.slice(0, 4).map((tag) => (
                      <Badge key={tag} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground flex items-center gap-1 pt-1">
                  <Calendar className="w-3.5 h-3.5" />
                  Submitted {new Date(job.createdAt).toLocaleDateString()}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </RecruiterPageShell>
  );
}
